import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { AppState, useAppState } from "../../contexts/App";
import { getPages } from "../../model/pages";
import { siteHasActivePlusSections } from "../../model/sectionDefinitions";
import { Site } from "../../model/site";
import { api, hasActiveSubscription } from "../../services/api";

/**
 * Leaving the style step, towards the last step ("ready to publish", the only one that publishes):
 * - never published: choosing its address (/editor/publish) first;
 * - PLUS (or FREE coming back from the PLUS page without PLUS sections): straight to it;
 * - FREE: the PLUS page, which leads to it (subscribed, or going on as FREE).
 * The draft is saved before leaving, so nothing edited is lost.
 */
export const usePublishFlow = (skipPlusOffer = false) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { appState, setAppState } = useAppState();

  const [isPublishing, setPublishing] = useState(false);
  const [showFreeKeeposLimitModal, setShowFreeKeeposLimitModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);

  const advance = async (site: Site) => {
    if (!appState.published) {
      navigate(`/editor/publish${skipPlusOffer ? "?skipPlusOffer=1" : ""}`);
      return;
    }

    setPublishing(true);
    setErrorMessage(undefined);
    try {
      await api.saveDraft(appState.editingSiteId as string, site);
      setAppState({ ...appState, editingSiteModified: true } as AppState);

      // with pages it always goes by the PLUS page, which tells that they stay off without PLUS
      const shouldSkipPlusOffer = skipPlusOffer && !siteHasActivePlusSections(site) && !getPages(site).length;
      navigate(
        isPlus || shouldSkipPlusOffer
          ? "/editor/publish/ready"
          : `/subscription/features?site=${appState.editingSiteId}&flow=edit`
      );
    } catch (e) {
      setErrorMessage(t("pages.editor.publish.errors.generic"));
      setPublishing(false);
    }
  };

  return {
    advance,
    isPublishing,
    errorMessage,
    /** Nothing is published here anymore: the last step does it */
    publishesNow: false,
    showFreeKeeposLimitModal,
    closeFreeKeeposLimitModal: () => setShowFreeKeeposLimitModal(false),
  };
};
